import { cn } from "@dune/ui";
import React from "react";
import {
	useGetFileUrlByBucketAndKey,
	useGetFileWithMetadata,
} from "../../api/queries/files.queries";
import { useDocumentStore } from "../../stores/document.store";
import { FileViewer } from "./FileViewer";
import { WordViewer } from "./WordViewer";

interface DocumentViewerProps {
	fileId?: string;
	url?: string;
	mimeType?: string;
	fileName?: string;
	className?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
	fileId,
	url,
	className = "",
	mimeType,
	fileName,
}) => {
	const storeFileId = useDocumentStore((state) => state.fileId);
	const actualFileId = fileId || (!url ? storeFileId : undefined);

	const { data: fileData, isLoading: isLoadingFile } = useGetFileWithMetadata(
		actualFileId || "",
	);

	const { data: fileUrl, isLoading: isLoadingUrl } =
		useGetFileUrlByBucketAndKey(fileData?.bucket, fileData?.key);

	const finalUrl = url || fileUrl;
	const finalMimeType = mimeType || fileData?.mimeType;
	const finalFileName = fileName || fileData?.name;

	const isLoadingData =
		!url && actualFileId && (isLoadingFile || isLoadingUrl || !finalUrl);

	if (isLoadingData) {
		return (
			<div
				className={cn(
					"h-full flex items-center justify-center bg-gray-50",
					className,
				)}
			>
				<div className="flex flex-col items-center justify-center space-y-4">
					<div className="relative w-12 h-12">
						<div className="absolute inset-0 border-3 border-gray-200 rounded-full" />
						<div className="absolute inset-0 border-3 border-transparent border-t-blue-600 rounded-full animate-spin" />
					</div>
					<p className="text-sm text-gray-500">Chargement du document...</p>
				</div>
			</div>
		);
	}

	if (!finalUrl) {
		return (
			<div
				className={cn(
					"h-full flex items-center justify-center bg-gray-50",
					className,
				)}
			>
				<div className="text-gray-500">Impossible de charger le document</div>
			</div>
		);
	}

	const wordMimeTypes = [
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	];
	const isWordByExtension = finalUrl.toLowerCase().match(/\.(doc|docx)($|\?)/);
	const isWordByMimeType =
		finalMimeType && wordMimeTypes.includes(finalMimeType);
	const isWordDocument = isWordByExtension || isWordByMimeType;

	if (isWordDocument) {
		return (
			<WordViewer
				url={finalUrl}
				fileName={finalFileName}
				className={cn("h-full", className)}
			/>
		);
	}

	const isPDF =
		finalMimeType === "application/pdf" ||
		finalUrl.toLowerCase().includes(".pdf");
	if (isPDF) {
		return (
			<iframe
				src={`${finalUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitV`}
				title={finalFileName || "Document PDF"}
				className={cn("w-full h-full border-0", className)}
				style={{
					minHeight: "84vh",
				}}
			/>
		);
	}

	if (
		finalMimeType !== "image/png" &&
		finalMimeType !== "image/jpeg" &&
		finalMimeType !== "image/jpg"
	) {
		return <div>Prévisualisation non disponible pour ce type de fichier</div>;
	}

	return (
		<FileViewer
			file={finalUrl}
			type={finalMimeType || ""}
			className={cn("h-full", className)}
		/>
	);
};
